/**
 * ActivityRing
 *
 * Animated ring around avatar showing activity/story status.
 * Creates a gradient ring that pulses when user has active stories.
 */
import { Box, BoxProps } from '@chakra-ui/react'
import { motion, AnimatePresence } from 'framer-motion'
import { useMemo } from 'react'
import {
  FavoriteCuisine,
  getDominantCuisineColor,
  getCuisineColorInfo,
} from '@/utils/cuisineColorMapping'

const MotionBox = motion.create(Box)

export interface ActivityRingProps extends Omit<BoxProps, 'children'> {
  /** Whether to show the ring (user has active stories) */
  isActive?: boolean
  /** Whether stories have not been viewed yet (triggers heartbeat animation) */
  hasUnviewed?: boolean
  /** Size of the avatar inside the ring */
  avatarSize?: number | string
  /** User's favorite cuisines for color customization */
  cuisines?: FavoriteCuisine[]
  /** Ring thickness in pixels */
  thickness?: number
  /** Gap between ring and avatar */
  gap?: number
  /** Click handler when ring is tapped */
  onClick?: () => void
  /** Children (typically the Avatar) */
  children: React.ReactNode
}

export function ActivityRing({
  isActive = false,
  hasUnviewed = false,
  avatarSize = 96,
  cuisines = [],
  thickness = 3,
  gap = 3,
  onClick,
  children,
  ...boxProps
}: ActivityRingProps) {
  // Generate ring gradient based on user's cuisines
  const ringGradient = useMemo(() => {
    if (!cuisines || cuisines.length === 0) {
      // Default Paire gradient
      return 'linear-gradient(135deg, var(--color-primary) 0%, #FF6B6B 33%, #FFE66D 66%, var(--color-primary) 100%)'
    }

    const colors = cuisines.slice(0, 3).map((c) => getCuisineColorInfo(c.name))
    if (colors.length === 1) {
      return `linear-gradient(135deg, ${colors[0].primaryColor} 0%, ${colors[0].secondaryColor} 50%, ${colors[0].accentColor} 100%)`
    }

    const colorStops = colors
      .map((c, i) => {
        const percent = Math.round((i / (colors.length - 1)) * 100)
        return `${c.primaryColor} ${percent}%`
      })
      .join(', ')

    return `linear-gradient(135deg, ${colorStops})`
  }, [cuisines])

  // Calculate total offset (ring + gap)
  const offset = thickness + gap

  return (
    <Box
      position="relative"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      {...boxProps}
    >
      {/* Animated ring background */}
      <AnimatePresence>
        {isActive && (
          <MotionBox
            position="absolute"
            top={`-${offset}px`}
            left={`-${offset}px`}
            right={`-${offset}px`}
            bottom={`-${offset}px`}
            borderRadius="full"
            background={ringGradient}
            cursor={onClick ? 'pointer' : 'default'}
            onClick={onClick}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={
              hasUnviewed
                ? {
                    opacity: 1,
                    scale: 1.05,
                  }
                : {
                    opacity: 1,
                    scale: 1,
                  }
            }
            exit={{ opacity: 0, scale: 0.95 }}
            transition={
              hasUnviewed
                ? {
                    duration: 1.2,
                    ease: 'easeInOut',
                    repeat: Infinity,
                    repeatType: 'mirror',
                  }
                : { duration: 0.3, ease: 'easeOut' }
            }
            _hover={onClick ? { transform: 'scale(1.02)' } : undefined}
          >
            {/* Inner pulse animation - stronger when unviewed */}
            <MotionBox
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              borderRadius="full"
              background={ringGradient}
              animate={{
                opacity: hasUnviewed ? 0.75 : 0.85,
              }}
              transition={{
                duration: hasUnviewed ? 1.2 : 2,
                ease: 'easeInOut',
                repeat: Infinity,
                repeatType: 'mirror',
              }}
            />
          </MotionBox>
        )}
      </AnimatePresence>

      {/* White spacer ring (creates gap between gradient and avatar) */}
      {isActive && (
        <Box
          position="absolute"
          top={`-${gap}px`}
          left={`-${gap}px`}
          right={`-${gap}px`}
          bottom={`-${gap}px`}
          borderRadius="full"
          bg="var(--color-bg-primary)"
          cursor={onClick ? 'pointer' : 'default'}
          onClick={onClick}
        />
      )}

      {/* Avatar container */}
      <Box
        position="relative"
        borderRadius="full"
        cursor={isActive && onClick ? 'pointer' : 'default'}
        onClick={isActive && onClick ? onClick : undefined}
        zIndex={1}
      >
        {children}
      </Box>
    </Box>
  )
}

/**
 * Simplified version that just shows a colored border
 * Good for smaller avatars or list items
 */
export function SimpleActivityRing({
  isActive = false,
  cuisines = [],
  thickness = 2,
  children,
  onClick,
  ...boxProps
}: Omit<ActivityRingProps, 'avatarSize' | 'gap'>) {
  const borderColor = useMemo(() => {
    if (!isActive) return 'transparent'
    return getDominantCuisineColor(cuisines)
  }, [isActive, cuisines])

  return (
    <Box
      borderRadius="full"
      border={`${thickness}px solid`}
      borderColor={isActive ? borderColor : 'transparent'}
      p={isActive ? '2px' : '0'}
      cursor={isActive && onClick ? 'pointer' : 'default'}
      onClick={onClick}
      transition="all 0.2s ease"
      {...boxProps}
    >
      {children}
    </Box>
  )
}

/**
 * Ring with spinning animation (for loading states or special effects)
 */
export function SpinningActivityRing({
  cuisines = [],
  thickness = 3,
  gap = 3,
  children,
  ...boxProps
}: Omit<ActivityRingProps, 'isActive' | 'onClick'>) {
  const ringGradient = useMemo(() => {
    const primaryColor = getDominantCuisineColor(cuisines)
    return `conic-gradient(from 0deg, ${primaryColor}, transparent 50%, ${primaryColor})`
  }, [cuisines])

  const offset = thickness + gap

  return (
    <Box
      position="relative"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      {...boxProps}
    >
      {/* Spinning gradient ring */}
      <MotionBox
        position="absolute"
        top={`-${offset}px`}
        left={`-${offset}px`}
        right={`-${offset}px`}
        bottom={`-${offset}px`}
        borderRadius="full"
        background={ringGradient}
        animate={{ rotate: 360 }}
        transition={{
          duration: 2,
          ease: 'linear',
          repeat: Infinity,
        }}
      />

      {/* White spacer */}
      <Box
        position="absolute"
        top={`-${gap}px`}
        left={`-${gap}px`}
        right={`-${gap}px`}
        bottom={`-${gap}px`}
        borderRadius="full"
        bg="var(--color-bg-primary)"
      />

      {/* Content */}
      <Box position="relative" borderRadius="full" zIndex={1}>
        {children}
      </Box>
    </Box>
  )
}
